import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterBySearchInput from '../../Filters/FilterBySearchInput';
import HeaderSearchResultsList from './HeaderSearchResultsList';

const DEBOUNCE_MS = 200;
const MIN_QUERY_LENGTH = 1;

const resultHref = (result) => {
	if (result.type === 'team') {
		return `/teams/${result.id}`;
	}

	return `/players/${result.id}?position=${encodeURIComponent(result.position)}`;
};

const HeaderSearch = ({ searchEndpoint }) => {
	const navigate = useNavigate();
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [results, setResults] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasSearched, setHasSearched] = useState(false);
	const [isInputVisible, setIsInputVisible] = useState(false);
	const [isResultsVisible, setIsResultsVisible] = useState(true);
	const [activeIndex, setActiveIndex] = useState(-1);
	const searchInputRef = useRef(null);
	const containerRef = useRef(null);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearch(search.trim());
		}, DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [search]);

	useEffect(() => {
		if (!searchEndpoint || debouncedSearch.length < MIN_QUERY_LENGTH) {
			setResults([]);
			setIsLoading(false);
			setHasSearched(false);
			setActiveIndex(-1);
			return undefined;
		}

		const abortController = new AbortController();

		const fetchResults = async () => {
			try {
				setIsLoading(true);
				const response = await fetch(
					`${searchEndpoint}?q=${encodeURIComponent(debouncedSearch)}`,
					{ signal: abortController.signal }
				);
				const payload = await response.json();
				setResults(payload.results || []);
				setHasSearched(true);
				setIsResultsVisible(true);
				setActiveIndex(-1);
			} catch (error) {
				if (error.name !== 'AbortError') {
					console.error('Error fetching header search results:', error);
					setResults([]);
					setHasSearched(true);
				}
			} finally {
				if (!abortController.signal.aborted) {
					setIsLoading(false);
				}
			}
		};

		fetchResults();

		return () => abortController.abort();
	}, [debouncedSearch, searchEndpoint]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsResultsVisible(false);
				setSearch('');
				setActiveIndex(-1);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const clearSearch = () => {
		setSearch('');
		setResults([]);
		setHasSearched(false);
		setIsResultsVisible(false);
		setActiveIndex(-1);
	};

	const selectResult = (result) => {
		if (!result) return;
		navigate(resultHref(result));
		clearSearch();
	};

	const handleSearchInputChange = (event) => {
		setSearch(event.target.value);
		setIsResultsVisible(true);
	};

	const handleKeyDown = (event) => {
		if (!isResultsVisible) return;

		if (event.key === 'ArrowDown') {
			event.preventDefault();
			if (results.length === 0) return;
			setActiveIndex((current) => (current + 1) % results.length);
			return;
		}

		if (event.key === 'ArrowUp') {
			event.preventDefault();
			if (results.length === 0) return;
			setActiveIndex((current) => (current <= 0 ? results.length - 1 : current - 1));
			return;
		}

		if (event.key === 'Escape') {
			clearSearch();
			return;
		}

		if (event.key === 'Enter') {
			event.preventDefault();
			if (activeIndex >= 0 && results[activeIndex]) {
				selectResult(results[activeIndex]);
			} else if (results[0]) {
				selectResult(results[0]);
			}
		}
	};

	const showDropdown =
		isResultsVisible &&
		debouncedSearch.length >= MIN_QUERY_LENGTH &&
		(isLoading || hasSearched);

	return (
		<div className="flex items-center relative" ref={containerRef}>
			<i
				className="fa-solid fa-magnifying-glass cursor-pointer text-white text-2xl py-5 pr-2.5"
				onClick={() => {
					setIsInputVisible((visible) => {
						const next = !visible;
						if (next) {
							requestAnimationFrame(() => searchInputRef.current?.focus());
						}
						return next;
					});
				}}
				aria-label="Toggle search"
			/>

			<div className={`ml-2 relative ${isInputVisible ? 'contents' : 'hidden'}`}>
				<FilterBySearchInput
					ref={searchInputRef}
					value={search}
					placeholder="Search players or teams"
					onChange={handleSearchInputChange}
					onKeyDown={handleKeyDown}
					isOnHeader={true}
					role="combobox"
					aria-autocomplete="list"
					aria-expanded={showDropdown}
					aria-controls="header-search-results"
				/>

				{showDropdown && (
					<HeaderSearchResultsList
						id="header-search-results"
						results={results}
						isLoading={isLoading}
						hasSearched={hasSearched}
						query={debouncedSearch}
						activeIndex={activeIndex}
						onSelect={selectResult}
						onHover={setActiveIndex}
					/>
				)}
			</div>
		</div>
	);
};

export default HeaderSearch;
